import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldAlert, TrendingUp, Bell, ThumbsUp, ThumbsDown, VolumeX, RotateCcw, Scale } from "lucide-react";
import GlobalBackButton from "@/components/GlobalBackButton";
import ObondoPaywall from "@/components/obondo/ObondoPaywall";
import ObondoOnboarding, { hasCompletedObondoOnboarding } from "@/components/obondo/ObondoOnboarding";
import ObondoChatPanel from "@/components/obondo/ObondoChatPanel";
import CompareTradeIdeasModal from "@/components/obondo/CompareTradeIdeasModal";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { hasInstitutionalFeatures } from "@/lib/tier";

/**
 * Obondo Coach — a chronological feed of every proactive push Obondo has
 * made for the trader (drawdown warnings, volatility spikes, post-mortems,
 * market-intel context notes). Users rate each push (helpful / not helpful
 * / mute this type) and can un-mute categories they previously silenced.
 *
 * Institutional-tier only — the backend returns 403 for individual users.
 */

const TYPE_META = {
    obondo_drawdown_daily_60:   { icon: ShieldAlert, label: "Daily DD · 60%",   accent: "text-amber-500" },
    obondo_drawdown_daily_80:   { icon: ShieldAlert, label: "Daily DD · 80%",   accent: "text-orange-500" },
    obondo_drawdown_daily_95:   { icon: ShieldAlert, label: "Daily DD · 95%",   accent: "text-red-500" },
    obondo_drawdown_max_60:     { icon: ShieldAlert, label: "Max DD · 60%",     accent: "text-amber-500" },
    obondo_drawdown_max_80:     { icon: ShieldAlert, label: "Max DD · 80%",     accent: "text-orange-500" },
    obondo_drawdown_max_95:     { icon: ShieldAlert, label: "Max DD · 95%",     accent: "text-red-500" },
    obondo_volatility_warn:     { icon: TrendingUp,  label: "Volatility warn",  accent: "text-amber-500" },
    obondo_volatility_urgent:   { icon: TrendingUp,  label: "Volatility urgent",accent: "text-red-500" },
    obondo_post_mortem:         { icon: Sparkles,    label: "Post-mortem",      accent: "text-primary" },
    obondo_market_intel:        { icon: Bell,        label: "Market intel",     accent: "text-blue-500" },
};

function relativeTime(iso) {
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const s = Math.floor((now - then) / 1000);
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch { return ""; }
}

export default function ObondoCoach() {
    const { user } = useAuth();
    // Feb 2026 paywall — see AiAssistant.jsx for the same policy.
    if (!hasInstitutionalFeatures(user)) return <ObondoPaywall product="Obondo Coach" />;
    return <ObondoCoachInner />;
}

function ObondoCoachInner() {
    const [items, setItems]           = useState([]);
    const [muted, setMuted]           = useState([]);
    const [coachTypes, setCoachTypes] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(() => !hasCompletedObondoOnboarding());
    const [showCompare, setShowCompare] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/obondo/coach/feed?limit=80");
            setItems(data.items || []);
            setMuted(data.muted_types || []);
            setCoachTypes(data.coach_types || []);
            setError(null);
        } catch (e) {
            if (e.response?.status === 403) {
                setError("Obondo Coach is available on institutional plans. Contact your institution administration for full access.");
            } else {
                setError("Could not load coach feed — please try again.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function feedback(id, verdict) {
        try {
            await api.post(`/obondo/coach/${id}/feedback`, { feedback: verdict });
            toast.success(verdict === "mute" ? "Muted this coaching type" : "Thanks — Obondo learns from this");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Feedback failed");
        }
    }

    async function unmute(coachType) {
        try {
            await api.post("/obondo/coach/unmute", { coach_type: coachType });
            toast.success(`${(TYPE_META[coachType]?.label || coachType)} re-enabled`);
            await load();
        } catch (e) {
            toast.error("Unmute failed");
        }
    }

    if (loading) return (
        <div className="max-w-4xl mx-auto p-6" data-testid="coach-loading">
            <div className="animate-pulse text-muted-foreground text-sm">Loading Obondo's coaching feed…</div>
        </div>
    );

    if (error) return (
        <div className="max-w-4xl mx-auto p-6" data-testid="coach-error">
            <GlobalBackButton />
            <div className="mt-6 border border-border rounded-xl p-8 bg-card text-center">
                <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
                <div className="text-lg font-bold mb-2">Obondo Coach</div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto p-6" data-testid="obondo-coach-page">
            <GlobalBackButton />

            {showOnboarding && (
                <ObondoOnboarding onComplete={() => setShowOnboarding(false)} />
            )}

            <div className="mt-6 mb-8" data-testid="obondo-coach-header">
                <div className="overline text-primary">Proactive coach</div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-2">Obondo Coach</h1>
                <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                    Every proactive nudge, warning, and post-mortem Obondo has fired for you.
                    Rate each one so Obondo learns which types to prioritise — or mute categories you find noisy.
                </p>
                <button
                    onClick={() => setShowOnboarding(true)}
                    className="mt-3 text-xs text-primary underline hover:no-underline"
                    data-testid="obondo-coach-replay-tour"
                >
                    Replay tour
                </button>
                <div className="mt-4">
                    <Button variant="outline" onClick={() => setShowCompare(true)} data-testid="obondo-coach-compare-ideas-btn">
                        <Scale className="w-4 h-4 mr-2" /> Compare up to 3 trade ideas
                    </Button>
                </div>
            </div>

            <CompareTradeIdeasModal open={showCompare} onClose={() => setShowCompare(false)} />

            <ObondoChatPanel />

            {muted.length > 0 && (
                <div className="mb-6 border border-border rounded-xl p-4 bg-muted/30" data-testid="coach-muted-strip">
                    <div className="overline mb-2 text-muted-foreground">Muted coaching types</div>
                    <div className="flex flex-wrap gap-2">
                        {muted.map((t) => {
                            const meta = TYPE_META[t] || { label: t, icon: VolumeX };
                            const Icon = meta.icon || VolumeX;
                            return (
                                <button
                                    key={t}
                                    onClick={() => unmute(t)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-border bg-background text-xs hover:bg-primary hover:text-primary-foreground transition"
                                    data-testid={`coach-unmute-${t}`}
                                >
                                    <Icon size={12} /> {meta.label} <RotateCcw size={10} className="ml-1" />
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <div className="border border-border rounded-xl p-10 bg-card text-center">
                    <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
                    <div className="font-semibold mb-1">No coaching pushes yet</div>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        Obondo watches your portfolio, drawdown, volatility, and market-news feed 24/7.
                        The moment something worth flagging shows up, it'll land here.
                    </p>
                </div>
            ) : (
                <div className="space-y-4" data-testid="coach-feed-list">
                    {items.map((it) => {
                        const meta = TYPE_META[it.type] || { icon: Sparkles, label: it.type, accent: "text-primary" };
                        const Icon = meta.icon;
                        const already = it.coach_feedback;
                        return (
                            <div
                                key={it.id}
                                className="border border-border rounded-xl p-5 bg-card card-hover"
                                data-testid={`coach-item-${it.id}`}
                            >
                                <div className="flex items-start gap-3 mb-2">
                                    <Icon size={20} className={meta.accent} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                                            <div className="font-bold text-base leading-tight">{it.title}</div>
                                            <div className="text-xs text-muted-foreground">{relativeTime(it.ts)} · {meta.label}</div>
                                        </div>
                                        <div className="text-sm whitespace-pre-line leading-relaxed text-foreground/90">
                                            {it.message}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/60">
                                    {already ? (
                                        <span className="text-xs text-muted-foreground italic" data-testid={`coach-feedback-done-${it.id}`}>
                                            You marked this as <b>{already.replace("_", " ")}</b> · thanks
                                        </span>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => feedback(it.id, "helpful")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border hover:border-primary hover:text-primary transition"
                                                data-testid={`coach-helpful-${it.id}`}
                                            >
                                                <ThumbsUp size={12} /> Helpful
                                            </button>
                                            <button
                                                onClick={() => feedback(it.id, "not_helpful")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border hover:border-muted-foreground transition"
                                                data-testid={`coach-nothelpful-${it.id}`}
                                            >
                                                <ThumbsDown size={12} /> Not helpful
                                            </button>
                                            <button
                                                onClick={() => feedback(it.id, "mute")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-border text-muted-foreground hover:border-danger hover:text-danger transition"
                                                data-testid={`coach-mute-${it.id}`}
                                            >
                                                <VolumeX size={12} /> Mute this type
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
